/**
 * @file ManagePermissions.jsx
 * @description Manage Permissions component for displaying and managing permissions.
 * @component ManagePermissions
 * @exports ManagePermissions - Main component for displaying and managing permissions.
 */

import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import SaveIcon from '@mui/icons-material/Save';
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from '@mui/material/LinearProgress';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import { Container, Typography, TextField, Button, Alert, Switch } from "@mui/material";
import { getMenuList, getRolePermissions, updateRolePermissions } from "../../../services/adminModule/permissions/permissionsServices.js";
import { VARIANTS, BUTTON_COLORS, SIZES, ALIGN, STATUS, AUTO_COMPLETE } from "../../../common/constants/commonConstants.js";
import { PERMISSION_STRINGS, PERMISSION_ERRORS, PERMISSION_MESSAGES, PAGE_PERMISSION_TABLE_COLUMNS, PAGE_PERMISSION_TABLE_COLUMN_KEYS } from "../../../common/constants/adminModule/permissionConstants.js";

const ManagePermissions = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { roleId } = useParams();
    const rolename = location.state?.rolename;
    const [userMenus, setUserMenus] = useState([]);
    const [searchPage, setSearchPage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loadingMenus, setLoadingMenus] = useState(true);
    const [warningMessage, setWarningMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [enabledMenuIds, setEnabledMenuIds] = useState(new Set());
    const [originalMenuIds, setOriginalMenuIds] = useState(new Set());
    const [expandedMenus, setExpandedMenus] = useState(new Set());

    useEffect(() => {
        loadMenusAndPermissions();
    }, []);

    const loadMenusAndPermissions = async () => {
        try {
            setLoadingMenus(true);
            const [menus, permissions] = await Promise.all([
                getMenuList(),
                getRolePermissions(roleId),
            ]);
            const enabledSet = new Set(permissions.map(p => p.menuid));
            setUserMenus(menus);
            setEnabledMenuIds(new Set(enabledSet));
            setOriginalMenuIds(new Set(enabledSet));
        } catch (error) {
            console.error(PERMISSION_ERRORS.FETCH_MENUS_ERROR, error);
        } finally {
            setLoadingMenus(false);
        }
    };

    const { pagesToDisplay } = useMemo(() => {
        const filteredMenus = userMenus.filter(menu =>
            menu.menuname.trim().toLowerCase().includes(searchPage.toLowerCase())
        );
        const childrenMap = {};
        filteredMenus.forEach(menu => {
            if (menu.parentid !== 0) {
                if (!childrenMap[menu.parentid]) {
                    childrenMap[menu.parentid] = [];
                }
                childrenMap[menu.parentid].push(menu);
            }
        });
        const orderedMenus = [];
        const topLevelMenus = filteredMenus
            .filter(menu => menu.parentid === 0)
            .sort((a, b) => a.id - b.id);
        for (const parent of topLevelMenus) {
            orderedMenus.push({ ...parent, isChild: false });
            const children = childrenMap[parent.id];
            if (children && expandedMenus.has(parent.id)) {
                children
                    .sort((a, b) => a.Campaignmenuorder - b.Campaignmenuorder)
                    .forEach(child => orderedMenus.push({ ...child, isChild: true }));
            }
        }
        return {
            pagesToDisplay: orderedMenus,
        };
    }, [userMenus, searchPage, expandedMenus]);

    const toggleAccess = (menuId) => {
        setEnabledMenuIds(prev => {
            const updatedMenuIds = new Set(prev);
            const currentMenu = userMenus.find(menu => menu.id === menuId);
            if (!currentMenu) return updatedMenuIds;
            const isParent = currentMenu.parentid === 0;
            const children = userMenus.filter(m => m.parentid === currentMenu.id);
            const parent = userMenus.find(m => m.id === currentMenu.parentid);
            const isCurrentlyEnabled = updatedMenuIds.has(menuId);
            if (isCurrentlyEnabled) {
                updatedMenuIds.delete(menuId);
                if (isParent) {
                    for (const child of children) {
                        updatedMenuIds.delete(child.id);
                    }
                } else {
                    const siblings = userMenus.filter(m => m.parentid === currentMenu.parentid);
                    const allSiblingsDisabled = siblings.every(sib => !updatedMenuIds.has(sib.id));
                    if (parent && allSiblingsDisabled) {
                        updatedMenuIds.delete(parent.id);
                    }
                }
            } else {
                updatedMenuIds.add(menuId);
                if (isParent) {
                    for (const child of children) {
                        updatedMenuIds.add(child.id);
                    }
                } else if (parent) {
                    updatedMenuIds.add(parent.id);
                }
            }
            return updatedMenuIds;
        });
    };

    const toggleExpandModule = (parentId) => {
        setExpandedMenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(parentId)) {
                newSet.delete(parentId);
            } else {
                newSet.add(parentId);
            }
            return newSet;
        });
    };

    const handleSavePermissions = async () => {
        const toAddAccess = [...enabledMenuIds].filter(id => !originalMenuIds.has(id));
        const toRemoveAccess = [...originalMenuIds].filter(id => !enabledMenuIds.has(id));
        if (toAddAccess.length === 0 && toRemoveAccess.length === 0) {
            setWarningMessage(PERMISSION_MESSAGES.NO_CHANGES);
            setTimeout(() => setWarningMessage(""), 3000);
            return;
        }
        try {
            await updateRolePermissions(roleId, { toAddAccess, toRemoveAccess });
            setSuccessMessage(PERMISSION_MESSAGES.PERMISSIONS_UPDATED);
            setTimeout(() => setSuccessMessage(""), 3000);
            setOriginalMenuIds(new Set(enabledMenuIds));
        } catch (error) {
            console.error(PERMISSION_ERRORS.PERMISSIONS_UPDATE_FAILED, error);
            setErrorMessage(PERMISSION_ERRORS.PERMISSIONS_UPDATE_FAILED);
            setTimeout(() => setErrorMessage(""), 3000);
        }
    };

    return (
        <>
            <Container maxWidth={false} disableGutters>
                <Grid container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
                    <Grid size="auto">
                        <IconButton
                            color={BUTTON_COLORS.SECONDARY}
                            onClick={() => navigate(-1)}
                        >
                            <ArrowCircleLeftOutlinedIcon />
                        </IconButton>
                    </Grid>
                    {rolename && (
                        <>
                            <Grid size="auto">
                                <Typography variant="h6">
                                    {`${rolename} ${PERMISSION_STRINGS.PERMISSIONS}`}
                                </Typography>
                            </Grid>
                        </>
                    )}
                </Grid>
                {warningMessage &&
                    <Alert severity={STATUS.WARNING} sx={{ mb: 2, justifyContent: "center" }}>
                        {warningMessage}
                    </Alert>
                }
                {errorMessage &&
                    <Alert severity={STATUS.ERROR} sx={{ mb: 2, justifyContent: "center" }}>
                        {errorMessage}
                    </Alert>
                }
                {successMessage &&
                    <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                        {successMessage}
                    </Alert>
                }
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={SIZES.GROW}>
                        <TextField
                            fullWidth
                            size={SIZES.SMALL}
                            value={searchPage}
                            variant={VARIANTS.OUTLINED}
                            autoComplete={AUTO_COMPLETE.OFF}
                            label={PERMISSION_STRINGS.SEARCH_PAGE}
                            onChange={e => setSearchPage(e.target.value)}
                        />
                    </Grid>
                    <Grid size={SIZES.AUTO}>
                        <Button
                            startIcon={<SaveIcon />}
                            variant={VARIANTS.CONTAINED}
                            color={BUTTON_COLORS.PRIMARY}
                            onClick={handleSavePermissions}
                        >
                            {PERMISSION_STRINGS.SAVE_PERMISSIONS}
                        </Button>
                    </Grid>
                </Grid>
                <TableContainer>
                    <Table size={SIZES.SMALL}>
                        <TableHead>
                            <TableRow>
                                {PAGE_PERMISSION_TABLE_COLUMNS.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.id === PAGE_PERMISSION_TABLE_COLUMN_KEYS.ACCESS ? ALIGN.RIGHT : ALIGN.LEFT}
                                        sx={{
                                            width:
                                                column.id === PAGE_PERMISSION_TABLE_COLUMN_KEYS.NAME ? 200 :
                                                column.id === PAGE_PERMISSION_TABLE_COLUMN_KEYS.TYPE ? 100 :
                                                column.id === PAGE_PERMISSION_TABLE_COLUMN_KEYS.ACCESS ? 50 : 200,
                                        }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingMenus ? (
                                <TableRow>
                                    <TableCell colSpan={PAGE_PERMISSION_TABLE_COLUMNS.length} align={ALIGN.CENTER}>
                                        <LinearProgress />
                                    </TableCell>
                                </TableRow>
                            ) : pagesToDisplay.length > 0 ? pagesToDisplay.map((userMenu) => (
                                <TableRow hover tabIndex={-1} key={userMenu.id} sx={userMenu.parentid === 0 ? { backgroundColor: "#ededed" } : {}}>
                                    <TableCell>
                                        {userMenu.parentid === 0 ? (
                                            <IconButton
                                                size={SIZES.SMALL}
                                                onClick={() => toggleExpandModule(userMenu.id)}
                                                style={{marginRight: 10}}
                                            >
                                                {expandedMenus.has(userMenu.id) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                            </IconButton>
                                        ) : (
                                            <span style={{ marginLeft: 45 }} />
                                        )}
                                        {userMenu.menuname}
                                    </TableCell>
                                    <TableCell>
                                        {userMenu.parentid === 0 ? PERMISSION_STRINGS.MODULE : PERMISSION_STRINGS.PAGE}
                                    </TableCell>
                                    <TableCell align={ALIGN.RIGHT}>
                                        <Switch
                                            onChange={() => toggleAccess(userMenu.id)}
                                            checked={enabledMenuIds.has(userMenu.id)}
                                        />
                                    </TableCell>
                                </TableRow>

                            )) : (
                                <TableRow>
                                    <TableCell colSpan={PAGE_PERMISSION_TABLE_COLUMNS.length} align={ALIGN.CENTER}>
                                        {PERMISSION_MESSAGES.NO_PAGES}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>
        </>
    );
};

export default ManagePermissions;
